import {
	type FC,
	type SVGProps,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CapacitorPassToWallet } from '@atroo/capacitor-pass-to-wallet';
import { Stack } from '@mui/material';
import * as Sentry from '@sentry/capacitor';

import { BusinessLayerContext, useUser } from '@mopla/business-logic';

import { Loader } from '../../atoms/loader/Loader';
import { MplButton } from '../../atoms/MplButton';
import { useMplSnackbar } from '../../hooks/useMplSnackbar';
import { MplErrorModal } from '../../organisms/MplModal';
import { MplSnackbar } from '../MplSnackbar/MplSnackbar';

import { loadAppleWalletBadge } from './AppleWalletBadge';
import { AppleWalletStyledButton } from './AppleWalletButton.styles';

interface IProps {
	ticketId: string;
}

interface ILazySvg {
	loading: boolean;
	error: Error | undefined;
	SVG: FC<SVGProps<SVGSVGElement>> | undefined;
}

const useLazyAppleWalletSvg = (): ILazySvg => {
	const { i18n } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error>();
	const Svg = useRef<FC<SVGProps<SVGSVGElement>>>();

	useEffect(() => {
		const language = i18n?.language || 'en';
		setLoading(true);
		(async () => {
			try {
				Svg.current = await loadAppleWalletBadge(language);
			} catch (e) {
				setError(e as Error);
			} finally {
				setLoading(false);
			}
		})();
	}, [i18n?.language]);

	return {
		loading,
		error,
		SVG: Svg.current,
	};
};

export const AppleWalletButton: FC<IProps> = ({ ticketId }) => {
	const { t } = useTranslation();
	const { userData } = useUser();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const { showSnackbar, snackbar, autoHideDuration, hideSnackbar } =
		useMplSnackbar();

	const businessLayer = useContext(BusinessLayerContext);
	const addToWalletDisabled = !userData?.canAddTicketToWallet;

	const {
		SVG: ButtonBadge,
		loading: SVGLoading,
		error: SVGError,
	} = useLazyAppleWalletSvg();

	const isProcessingRef = useRef(false);

	const handleClick = async () => {
		if (isProcessingRef.current) return;
		isProcessingRef.current = true;

		try {
			if (addToWalletDisabled) {
				showSnackbar(t('dticket:text.add_to_wallet_unavailable'));
				return;
			}

			setLoading(true);
			setError(false);

			const response = await businessLayer.api.makeRequest<ArrayBuffer>(
				`/api/passengers/appleWalletPass?ticketId=${ticketId}`,
				{ responseType: 'arraybuffer' }
			);

			// btoa is deprecated, but buffer methods are not available in Capacitor
			const base64String = btoa(
				String.fromCharCode(...new Uint8Array(response.data))
			);

			await CapacitorPassToWallet.addToWallet({ base64: base64String });
		} catch (e) {
			Sentry.captureException(e);
			setError(true);
		} finally {
			setLoading(false);
			isProcessingRef.current = false;
		}
	};

	if (loading || SVGLoading) return <Loader />;
	if (!userData) return null;

	return (
		<>
			{ButtonBadge && (
				<Stack width="100%" alignItems="center">
					<AppleWalletStyledButton
						type="button"
						aria-disabled={addToWalletDisabled}
						aria-label={t('dticket:text.add_to_apple_wallet')}
						onClick={handleClick}
						disabled={loading || snackbar.open}
						data-testid="apple-wallet-button"
					>
						<ButtonBadge aria-hidden style={{ height: '48px' }} />
					</AppleWalletStyledButton>
				</Stack>
			)}

			<MplSnackbar
				open={snackbar.open}
				message={snackbar.message}
				onClose={hideSnackbar}
				autoHideDuration={autoHideDuration}
				dataTestId="add-to-wallet-unavailable-snackbar"
			/>

			<MplErrorModal
				open={error || Boolean(SVGError)}
				testId="AppleWalletButton-MplErrorModal"
				buttons={
					<MplButton fullWidth onClick={() => setError(false)}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
		</>
	);
};
